import React from 'react';
import {Image, TextInput, TouchableOpacity} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import Ionicons from 'react-native-vector-icons/Ionicons';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {CheckeredStrip} from '@/Components/ChessModal';
import {moveAnnotations} from '@/Utils/CommonUtils';
import {styles} from '../../styles';
import {
  ANNOTATIONS_COLUMNS,
  ANNOTATIONS_PER_COLUMN,
} from '../../constants/annotationsGrid';
import {commentAccentColor} from '../../utils/studyHelpers';
import {LessonMoveRole} from '../../types';

type AnnotationsPopoverProps = {
  entered: boolean;
  selectedIcon: string | undefined;
  onSelectIcon: (name: string) => void;
};

// Grid of annotation glyphs, like Lichess's own move-annotation picker -
// name + symbol tiles, 4 columns x 6 rows - a compact grid sized to the
// glyph set's exact count (24), styled with the same dark-walnut header +
// checkered strip + cream body language as the school-icon popover, so
// every board-toolbar popover reads as one family.
export function AnnotationsPopover({
  entered,
  selectedIcon,
  onSelectIcon,
}: AnnotationsPopoverProps) {
  return (
    <Box
      style={[
        styles.boardToolPopover,
        styles.lessonPopover,
        {width: 300},
        {
          opacity: entered ? 1 : 0,
          transform: [{translateY: entered ? 0 : 8}],
        },
      ]}>
      <Box style={styles.lessonPopoverHeader}>
        <Box style={styles.lessonPopoverHeaderIcon}>
          <CCText fontSize={11} fontWeight="bold" color="#FACF47">
            !?
          </CCText>
        </Box>
        <Box style={{flex: 1}}>
          <CCText fontSize={12.5} fontWeight="bold" color={CCColors.White}>
            Annotate move
          </CCText>
          <CCText fontSize={10} color="rgba(255,255,255,0.5)">
            Tag this move's quality or idea
          </CCText>
        </Box>
        <TouchableOpacity onPress={() => onSelectIcon('')}>
          <CCText fontSize={10} color="#FF8A80" fontWeight="semibold">
            Clear
          </CCText>
        </TouchableOpacity>
      </Box>
      <CheckeredStrip height={5} />
      <Box style={[styles.lessonPopoverBody, {paddingHorizontal: 10}]}>
        <Box style={{flexDirection: 'row', gap: 6}}>
          {Array.from({length: ANNOTATIONS_COLUMNS}).map((_, colIdx) => (
            <Box key={colIdx} style={{flex: 1, gap: 6}}>
              {moveAnnotations
                ?.slice(
                  colIdx * ANNOTATIONS_PER_COLUMN,
                  colIdx * ANNOTATIONS_PER_COLUMN + ANNOTATIONS_PER_COLUMN,
                )
                .map((item, idx) => {
                  const isSelected = selectedIcon === item?.name;
                  // Not yet seeded on the backend (AnnotationIcon lookup in
                  // update_annotation would 404) - show it greyed out and
                  // unselectable until a migration adds it. Remove this
                  // once that ships.
                  const isUnavailable = item?.name === 'Novelty';
                  return (
                    <TouchableOpacity
                      key={idx}
                      onPress={() => !isUnavailable && onSelectIcon(item.name)}
                      disabled={isUnavailable}
                      accessibilityLabel={item.name}
                      accessibilityState={{disabled: isUnavailable}}
                      style={[
                        styles.annotationTile,
                        isSelected && styles.annotationTileActive,
                        isUnavailable && {opacity: 0.35},
                      ]}>
                      <Image
                        resizeMode="contain"
                        style={{width: 14, height: 14}}
                        source={isSelected ? item?.white_icon : item?.icon}
                      />
                      <CCText
                        fontSize={7.5}
                        fontWeight="medium"
                        numberOfLines={2}
                        textAlign="center"
                        color={
                          isSelected ? CCColors.White : CCColors.Workbook.Brown
                        }
                        style={{marginTop: 3, lineHeight: 9}}>
                        {item.name}
                      </CCText>
                    </TouchableOpacity>
                  );
                })}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

type LessonFieldProps = {
  icon: string;
  accent: string;
  label: string;
  caption: string;
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  multiline?: boolean;
};

// One labeled input in the lesson popover: a small colored icon + an
// uppercase micro-label (matching the "Add Chapter" popup's field
// labels), the input itself, and an optional muted caption below it.
function LessonField({
  icon,
  accent,
  label,
  caption,
  value,
  onChangeText,
  placeholder,
  multiline,
}: LessonFieldProps) {
  return (
    <Box style={styles.lessonFieldGroup}>
      <Box style={styles.lessonFieldLabelRow}>
        <MaterialCommunityIcons name={icon} size={12} color={accent} />
        <CCText style={styles.lessonFieldLabel}>{label}</CCText>
      </Box>
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={CCColors.TextColor.Grey}
        multiline={multiline}
        numberOfLines={multiline ? 2 : undefined}
        style={[
          styles.lessonInput,
          multiline && {height: 52, paddingTop: 8, paddingBottom: 8},
          {borderLeftWidth: 3, borderLeftColor: accent},
        ]}
      />
      <CCText style={styles.lessonFieldHint}>{caption}</CCText>
    </Box>
  );
}

export type LessonPopoverProps = {
  entered: boolean;
  chapterMode: string | undefined;
  chapterOrientation: string | undefined;
  moveSelected: any;
  autoSaveStatus: 'idle' | 'saving' | 'saved' | string;
  // The plain free-text comment - used for a non-lesson chapter's move
  // comment, an opponent (auto-played) move's reflection, and a lesson
  // "Mistake/Blunder" branch's wrong-move explanation.
  annotationDraft: string;
  onChangeAnnotationDraft: (text: string) => void;
  selectedLessonRole: LessonMoveRole;
  isMainlineSelected: boolean;
  onSetLessonRole: (role: LessonMoveRole) => void;
  perfectFieldTab: 'success' | 'hint' | 'deviation';
  onSetPerfectFieldTab: (tab: 'success' | 'hint' | 'deviation') => void;
  successMessage: string;
  onChangeSuccessMessage: (text: string) => void;
  hint: string;
  onChangeHint: (text: string) => void;
  deviationMessage: string;
  onChangeDeviationMessage: (text: string) => void;
};

// Lesson hint/success/deviation authoring - the whole "what does the
// coach need to say about this move" workflow: role, success message,
// hint, wrong-move feedback, or just a plain comment outside lesson mode.
// Styled with the same dark-walnut header + gold accent + checkered strip
// language as the "Add Chapter" popup (ChessModalShell).
export function LessonPopover({
  entered,
  chapterMode,
  chapterOrientation,
  moveSelected,
  autoSaveStatus,
  annotationDraft,
  onChangeAnnotationDraft,
  selectedLessonRole,
  isMainlineSelected,
  onSetLessonRole,
  perfectFieldTab,
  onSetPerfectFieldTab,
  successMessage,
  onChangeSuccessMessage,
  hint,
  onChangeHint,
  deviationMessage,
  onChangeDeviationMessage,
}: LessonPopoverProps) {
  const isLessonChapterMode = chapterMode === 'lesson';
  // Node-type-aware authoring, matching lila's gamebookEdit: the fields
  // below describe "guidance for reaching *this* move" - they are shown
  // to the student at this move's *parent* position. Opponent moves
  // (auto-played for the student) don't have a "find this move" moment,
  // so they only take a plain comment, not hint/success/deviation.
  const studentColor = chapterOrientation === 'B' ? 'b' : 'w';
  const moverColor = moveSelected?.lastFen?.split(' ')[1];
  const isStartNode = !moveSelected?.id || moveSelected.id === 'start';
  const isOpponentMoveNode =
    !isStartNode && moverColor && moverColor !== studentColor;

  const autoSaveLabel =
    autoSaveStatus === 'saving'
      ? 'Saving…'
      : autoSaveStatus === 'saved'
      ? 'Saved'
      : '';

  const commentField = (label: string, placeholder: string) => (
    <Box style={styles.lessonFieldGroup}>
      <CCText style={styles.lessonFieldLabel}>{label}</CCText>
      <TextInput
        value={annotationDraft}
        onChangeText={onChangeAnnotationDraft}
        placeholder={placeholder}
        placeholderTextColor={CCColors.TextColor.Grey}
        style={[
          styles.lessonInput,
          styles.lessonSingleLineInput,
          annotationDraft && {color: commentAccentColor(annotationDraft)},
        ]}
      />
    </Box>
  );

  const shell = (
    title: string,
    subtitle: string | null,
    body: React.ReactNode,
    shellEntered = true,
  ) => (
    <Box
      style={[
        styles.boardToolPopover,
        styles.lessonPopover,
        {width: 300},
        {
          opacity: shellEntered ? 1 : 0,
          transform: [{translateY: shellEntered ? 0 : 8}],
        },
      ]}>
      <Box style={styles.lessonPopoverHeader}>
        <Box style={styles.lessonPopoverHeaderIcon}>
          <MaterialCommunityIcons
            name="school-outline"
            size={14}
            color="#FACF47"
          />
        </Box>
        <Box style={{flex: 1}}>
          <CCText fontSize={12.5} fontWeight="bold" color={CCColors.White}>
            {title}
          </CCText>
          {!!subtitle && (
            <CCText fontSize={10} color="rgba(255,255,255,0.5)">
              {subtitle}
            </CCText>
          )}
        </Box>
        {!!autoSaveLabel && (
          <CCText fontSize={10} color="#FACF47">
            {autoSaveLabel}
          </CCText>
        )}
      </Box>
      <CheckeredStrip height={5} />
      <Box style={styles.lessonPopoverBody}>{body}</Box>
    </Box>
  );

  if (isStartNode) {
    return shell(
      'Move guidance',
      null,
      <CCText
        fontSize={11}
        color={CCColors.Workbook.Brown}
        style={{lineHeight: 16}}>
        {isLessonChapterMode
          ? 'Select a move in the move list to set its role and author its hint, success message, and wrong-move feedback.'
          : 'Select a move in the move list to add a comment on it.'}
      </CCText>,
      false,
    );
  }

  // Outside a lesson chapter, every node just gets the plain comment
  // field - no role/hint/success/deviation concepts apply.
  if (!isLessonChapterMode) {
    return shell(
      'Move comment',
      moveSelected?.san,
      commentField('Comment', 'Add your comment on this move...'),
    );
  }

  if (isOpponentMoveNode) {
    return shell(
      'Opponent move',
      moveSelected?.san,
      commentField(
        'Comment (shown alongside this move)',
        'Reflect on the position this creates...',
      ),
    );
  }

  // Player move node in a lesson chapter: the tab bar *is* the role
  // selector (Perfect / Alternate / Mistake) now - one tap both sets the
  // role and switches to the fields it needs. Perfect is the only role
  // with more than one field (hint + success + wrong-move feedback all
  // apply to the expected line), so those three tab under it rather than
  // needing yet another sub-tab layer.
  const roleTabs: Array<{
    key: LessonMoveRole;
    label: string;
    icon: string;
    accent: string;
  }> = [
    {key: 'perfect', label: 'Perfect', icon: 'star', accent: CCColors.Green},
    {
      key: 'alternate',
      label: 'Alternate',
      icon: 'git-branch-outline',
      accent: CCColors.Progress.Bar,
    },
    {
      key: 'bad',
      label: 'Mistake',
      icon: 'close-circle-outline',
      accent: CCColors.Error,
    },
  ];

  const perfectFieldTabs: Array<{
    key: 'success' | 'hint' | 'deviation';
    label: string;
    icon: string;
    accent: string;
  }> = [
    {
      key: 'success',
      label: 'Success',
      icon: 'check-circle-outline',
      accent: CCColors.Workbook.Success,
    },
    {
      key: 'hint',
      label: 'Hint',
      icon: 'lightbulb-outline',
      accent: CCColors.Workbook.Gold,
    },
    {
      key: 'deviation',
      label: 'Wrong move',
      icon: 'close-circle-outline',
      accent: CCColors.Workbook.DangerRed,
    },
  ];

  return shell(
    'Move guidance',
    moveSelected?.san,
    <>
      <Box style={styles.lessonTabRow}>
        {roleTabs.map(tab => {
          const isActive = selectedLessonRole === tab.key;
          const isDisabled = isMainlineSelected && tab.key !== 'perfect';
          return (
            <TouchableOpacity
              key={tab.key}
              disabled={isDisabled}
              onPress={() => onSetLessonRole(tab.key)}
              style={[
                styles.lessonTab,
                isActive && {
                  borderColor: tab.accent,
                  backgroundColor: updateColorWithOpacity(tab.accent, 0.1),
                },
                isDisabled && {opacity: 0.4},
              ]}>
              <Ionicons
                name={tab.icon as any}
                size={12}
                color={isActive ? tab.accent : CCColors.Workbook.Grey}
              />
              <CCText
                style={[
                  styles.lessonTabLabel,
                  isActive && {color: tab.accent},
                ]}>
                {tab.label}
              </CCText>
            </TouchableOpacity>
          );
        })}
      </Box>
      <CCText style={styles.lessonHelperText}>
        {isMainlineSelected
          ? 'This is the expected line. To demote it, promote a branch to mainline instead.'
          : selectedLessonRole === 'bad'
          ? 'Played by the student, this counts as wrong and shows your comment below.'
          : 'An acceptable sideline — the student may play it without being marked wrong.'}
      </CCText>

      {selectedLessonRole === 'bad' ? (
        commentField(
          'Why is this move wrong?',
          'e.g. This drops the knight — look at the pin on the e-file.',
        )
      ) : selectedLessonRole === 'perfect' ? (
        // Perfect is the only role with three guidance fields (Success/
        // Hint/Wrong-move all apply to the expected line) - tabbed rather
        // than stacked so the popover doesn't grow three TextInputs tall.
        <>
          <Box style={styles.lessonTabRow}>
            {perfectFieldTabs.map(tab => {
              const isActive = perfectFieldTab === tab.key;
              return (
                <TouchableOpacity
                  key={tab.key}
                  onPress={() => onSetPerfectFieldTab(tab.key)}
                  style={[
                    styles.lessonTab,
                    isActive && {
                      borderColor: tab.accent,
                      backgroundColor: updateColorWithOpacity(tab.accent, 0.1),
                    },
                  ]}>
                  <Ionicons
                    name={tab.icon as any}
                    size={12}
                    color={isActive ? tab.accent : CCColors.Workbook.Grey}
                  />
                  <CCText
                    style={[
                      styles.lessonTabLabel,
                      isActive && {color: tab.accent},
                    ]}>
                    {tab.label}
                  </CCText>
                </TouchableOpacity>
              );
            })}
          </Box>
          {perfectFieldTab === 'success' && (
            <LessonField
              icon="check-circle-outline"
              accent={CCColors.Workbook.Success}
              label="Success message"
              caption="Shown when the student plays this move."
              value={successMessage}
              onChangeText={onChangeSuccessMessage}
              placeholder="e.g. Great job! That controls the center."
            />
          )}
          {perfectFieldTab === 'hint' && (
            <LessonField
              icon="lightbulb-outline"
              accent={CCColors.Workbook.Gold}
              label="Hint"
              caption="Shown on request while the student is finding this move."
              value={hint}
              onChangeText={onChangeHint}
              placeholder="e.g. Try to control the center with your pawn."
            />
          )}
          {perfectFieldTab === 'deviation' && (
            <LessonField
              icon="close-circle-outline"
              accent={CCColors.Workbook.DangerRed}
              label="Wrong-move feedback"
              caption="Shown for any wrong move that has no comment of its own."
              value={deviationMessage}
              onChangeText={onChangeDeviationMessage}
              placeholder="e.g. That move loses a pawn — look for a way to defend it."
              multiline
            />
          )}
        </>
      ) : (
        <LessonField
          icon="check-circle-outline"
          accent={CCColors.Workbook.Success}
          label="Success message"
          caption="Shown when the student plays this move."
          value={successMessage}
          onChangeText={onChangeSuccessMessage}
          placeholder="e.g. Great job! That controls the center."
        />
      )}
    </>,
    entered,
  );
}
